// import TagManager from 'react-gtm-module';
import PhoneCode from 'pages/PhoneCode/PhoneCode';
import { AsyncFinance } from 'pages/Registration/Finance/async';
import { ReactNode } from 'react';
import {
  Navigate,
  Outlet,
  Route,
  RouterProvider,
  createRoutesFromElements,
} from 'react-router';
import { createBrowserRouter } from 'datadog/rum';
import GuardRoute from './GuardRoute';

import * as R from './routes';
import styles from './Router.module.scss';
import DashboardRedirect from 'pages/Login/DashboardRedirect';
import OfflinePSD2Token from 'pages/Registration/OfflinePSD2Token/OfflinePSD2Token';
import {
  AdjustIncome,
  Approved,
  BankIdConfirm,
  BankIdError,
  BankIdInfo,
  DocumentUploadLater,
  Intro,
  NotFound,
  PhoneChange,
  PhoneVerification,
  Registration,
  Upsale,
} from 'pages';
import ContinueAffiliate from 'pages/ContinueAffiliate/ContinueAffiliate';
import PersonalDetailsContinue from 'pages/Registration/PersonalDetails/PersonalDetailsContinue/PersonalDetailsContinue';
import OfflinePSD2Verified from 'pages/Registration/OfflinePSD2Verified/OfflinePSD2Verified';
import OfflinePSD2Error from 'pages/Registration/OfflinePSD2Error/OfflinePSD2Error';
import RequireAuth from 'Router/RequireAuth';
import Downsale from 'pages/Registration/Downsale/Downsale';
import RejectedOffer from 'pages/Registration/RejectedOffer/RejectedOffer';
import Agreement from 'pages/Registration/Agreement/Agreement';
import OCRResults from 'pages/Registration/OCRResults/OCRResults';
import Manual from 'pages/Registration/ManualOCR/Manual';
import PSD2Guide from 'pages/Registration/PSD2Guide/PSD2Guide';
import PSD2Consents from 'pages/Registration/PSD2Consents/PSD2Consents';
import PSD2Receiving from 'pages/Registration/PSD2Receiving/PSD2Receiving';
import PSD2Verified from 'pages/Registration/PSD2Verified/PSD2Verified';
import PSD2Error from 'pages/Registration/PSD2Error/PSD2Error';
import ProofIncome from 'pages/Registration/ProofIncome/ProofIncome';
import useGoogleTagManager from 'hooks/useGoogleTagManager';
import Waiting from 'pages/Waiting/Waiting';
import ManualReview from 'pages/Registration/ManualReview/ManualReview';
import Login from 'pages/Login/Login';
import DataLayer from 'utils/dataLayer/DataLayer';
import WelcomeBackIdentificationStatus from 'pages/Registration/WelcomeBack/WelcomeBackIdentificationStatus';
import WelcomeBackNewApplication from 'pages/Registration/WelcomeBack/WelcomeBackNewApplication';
import BankIdAccountSelection from 'pages/Registration/BankId/BankIdAccountSelection/BankIdAccountSelection';
import PSD2BanksList from 'pages/Registration/PSD2Banks/PSD2Banks';
import PSD2AccountSelection from 'pages/Registration/PSD2SelectBankAccount/PSD2AccountSelection';
import UpsaleChangeAmount from 'pages/Registration/UpsaleChangeAmount/UpsaleChangeAmount';
import ManualOCR from 'pages/Registration/ManualOCR/ManualOCR';
import ManualUploadID from 'pages/Registration/ManualUploadID/ManualUploadID';
import AffiliatePage from 'pages/Affiliate/Affiliate';
import PageTracker from 'hooks/context/EventCollector/usePageTracker';
import VccLiveChat from 'components/VccLiveChat/VccLiveChat';
import { VCC_LIVE_CHAT } from '../constants';
import BackButtonBlocker from '../utils/BackButtonBlocker';
import { PersonalDetailsResolver } from '../pages/Registration/PersonalDetails/PersonalDetailsRoute';
import { AsyncOneTimeTokenLogin } from '../pages/Login/async';
import { TestHelperWrapper } from 'utils/TestHelper/TestHelperWrapper';
import BankIdWaiting from 'pages/Registration/BankId/BankIdWaiting/BankIdWaiting';

function RouteWrapper() {
  const isChatEnabled = import.meta.env.VITE_CHAT_ENABLED !== 'false';

  return (
    <>
      <BackButtonBlocker />
      <PageTracker />
      <DataLayer />
      <Outlet />
      <TestHelperWrapper />
      {isChatEnabled && <VccLiveChat accountId={VCC_LIVE_CHAT.ACCOUNT_ID} />}
    </>
  );
}

function MultiRoute(paths: string[], component: ReactNode) {
  return paths.map((path) => (
    <Route key={path} element={component} path={path} />
  ));
}

const router = createBrowserRouter(
  createRoutesFromElements(
    <Route element={<RouteWrapper />}>
      {/* Route should not be used as redirect specified on login itself based on user state */}
      <Route path={R.DASHBOARD_REDIRECT} element={<DashboardRedirect />} />
      <Route
        path={R.LEGACY_LOGIN_ONE_TIME_TOKEN_LOGIN}
        element={<AsyncOneTimeTokenLogin />}
      />
      <Route element={<OfflinePSD2Token />} path={R.OFFLINE_TOKEN} />
      <Route element={<AffiliatePage />} path={R.AFFILIATE} />
      <Route element={<Registration />} path="/">
        <Route path={R.LOGIN} element={<Login />} />
        <Route
          element={
            <RequireAuth>
              <WelcomeBackIdentificationStatus />
            </RequireAuth>
          }
          path={R.WELCOME_BACK}
        />
        <Route
          element={
            <RequireAuth>
              <WelcomeBackNewApplication />
            </RequireAuth>
          }
          path={R.WELCOME_BACK_NEW}
        />
        {MultiRoute([R.INTRO, R.INTRO_START_APP, R.INTRO_2], <Intro />)}
        <Route element={<ContinueAffiliate />} path={R.CONTINUE_AFFILIATE} />
        <Route
          element={<PersonalDetailsResolver />}
          path={R.PERSONAL_DETAILS}
        />
        <Route
          element={<PersonalDetailsContinue />}
          path={R.PERSONAL_DETAILS_CONTINUE}
        />
        <Route
          element={
            <RequireAuth>
              <GuardRoute>
                <AsyncFinance />
              </GuardRoute>
            </RequireAuth>
          }
          path={R.FINANCE}
        />
        <Route
          element={<PSD2BanksList />}
          path={`${R.OFFLINE_PSD2_BANKS}/:token`}
        />
        <Route
          path={`${R.OFFLINE_PSD2_GUIDE}/:token`}
          element={<PSD2Guide />}
        />
        <Route
          path={`${R.OFFLINE_PSD2_CONSENTS}/:token`}
          element={<PSD2Consents />}
        />
        {/* NOTE: Workaround because of back-end */}
        <Route
          path={R.OFFLINE_PSD2_BANK_ACCOUNT_SELECT}
          element={<PSD2AccountSelection />}
        />
        <Route
          path={`${R.OFFLINE_PSD2_BANK_ACCOUNT_SELECT}/:token`}
          element={<PSD2AccountSelection />}
        />
        {/* NOTE: Workaround because of back-end */}
        <Route path={R.OFFLINE_PSD2_RECEIVING} element={<PSD2Receiving />} />
        <Route
          path={`${R.OFFLINE_PSD2_RECEIVING}/:token`}
          element={<PSD2Receiving />}
        />
        <Route
          path={`${R.OFFLINE_PSD2_VERIFIED}/:token`}
          element={<OfflinePSD2Verified />}
        />
        <Route
          path={`${R.OFFLINE_PSD2_ERROR}/:token`}
          element={<OfflinePSD2Error />}
        />
        <Route
          path={R.BANK_ID}
          element={
            <RequireAuth>
              <GuardRoute>
                <BankIdInfo />
              </GuardRoute>
            </RequireAuth>
          }
        />
        <Route
          path={R.BANK_ID_WAIT}
          element={
            <RequireAuth>
              <GuardRoute>
                <BankIdWaiting />
              </GuardRoute>
            </RequireAuth>
          }
        />
        <Route
          path={R.BANK_ID_CONFIRM}
          element={
            <RequireAuth>
              <GuardRoute>
                <BankIdConfirm />
              </GuardRoute>
            </RequireAuth>
          }
        />
        <Route
          path={R.BANK_ID_ACCOUNT_SELECTION}
          element={
            <RequireAuth>
              <GuardRoute>
                <BankIdAccountSelection />
              </GuardRoute>
            </RequireAuth>
          }
        />
        <Route
          path={R.BANK_ID_ERROR}
          element={
            <RequireAuth>
              <GuardRoute>
                <BankIdError />
              </GuardRoute>
            </RequireAuth>
          }
        />
        <Route
          element={
            <RequireAuth>
              <GuardRoute>
                <PhoneChange />
              </GuardRoute>
            </RequireAuth>
          }
          path={R.PHONE_CHANGE}
        />
        <Route
          element={
            <RequireAuth>
              <GuardRoute>
                <PhoneVerification />
              </GuardRoute>
            </RequireAuth>
          }
          path={R.PHONE_VERIFICATION}
        />
        <Route
          element={
            <RequireAuth>
              <GuardRoute>
                <ManualOCR />
              </GuardRoute>
            </RequireAuth>
          }
          path={R.MANUAL_OCR}
        />
        <Route
          element={
            <RequireAuth>
              <GuardRoute>
                <Downsale />
              </GuardRoute>
            </RequireAuth>
          }
          path={R.DOWNSALE}
        />
        <Route
          element={
            <RequireAuth>
              <GuardRoute>
                <Upsale />
              </GuardRoute>
            </RequireAuth>
          }
          path={R.UPSALE}
        />
        <Route
          element={
            <RequireAuth>
              <GuardRoute>
                <UpsaleChangeAmount />
              </GuardRoute>
            </RequireAuth>
          }
          path={R.UPSALE_CHANGE_AMOUNT}
        />
        <Route
          element={
            <RequireAuth>
              <GuardRoute>
                <RejectedOffer />
              </GuardRoute>
            </RequireAuth>
          }
          path={R.REJECTED_OFFER}
        />
        <Route
          element={
            <RequireAuth>
              <GuardRoute>
                <Agreement />
              </GuardRoute>
            </RequireAuth>
          }
          path={R.CONSENT}
        />
        <Route
          element={
            <RequireAuth>
              <GuardRoute>
                <PhoneCode />
              </GuardRoute>
            </RequireAuth>
          }
          path={R.SIGN_AGREEMENT}
        />
        <Route
          element={
            <RequireAuth>
              <GuardRoute>
                <OCRResults />
              </GuardRoute>
            </RequireAuth>
          }
          path={R.OCR_RESULTS}
        />
        <Route
          element={
            <RequireAuth>
              <GuardRoute>
                <ManualUploadID />
              </GuardRoute>
            </RequireAuth>
          }
          path={R.MANUAL_UPLOAD_ID}
        />
        <Route
          element={
            <RequireAuth>
              <GuardRoute>
                <Manual />
              </GuardRoute>
            </RequireAuth>
          }
          path={R.MANUAL_IDENTIFICATION}
        />
        <Route
          element={
            <RequireAuth>
              <GuardRoute>
                <PSD2BanksList />
              </GuardRoute>
            </RequireAuth>
          }
          path={R.MANUAL_PSD2_BANKS}
        />
        <Route
          path={R.MANUAL_PSD2_GUIDE}
          element={
            <RequireAuth>
              <GuardRoute>
                <PSD2Guide />
              </GuardRoute>
            </RequireAuth>
          }
        />
        <Route
          path={R.MANUAL_PSD2_CONSENTS}
          element={
            <RequireAuth>
              <GuardRoute>
                <PSD2Consents />
              </GuardRoute>
            </RequireAuth>
          }
        />
        <Route
          element={
            <RequireAuth>
              <GuardRoute>
                <PSD2BanksList />
              </GuardRoute>
            </RequireAuth>
          }
          path={R.PSD2_BANKS}
        />
        <Route
          path={R.PSD2_GUIDE}
          element={
            <RequireAuth>
              <GuardRoute>
                <PSD2Guide />
              </GuardRoute>
            </RequireAuth>
          }
        />
        <Route
          path={R.PSD2_CONSENTS}
          element={
            <RequireAuth>
              <GuardRoute>
                <PSD2Consents />
              </GuardRoute>
            </RequireAuth>
          }
        />
        <Route
          path={R.PSD2_BANK_ACCOUNT_SELECT}
          element={
            <RequireAuth>
              <GuardRoute>
                <PSD2AccountSelection />
              </GuardRoute>
            </RequireAuth>
          }
        />
        <Route
          path={R.PSD2_RECEIVING}
          element={
            <RequireAuth>
              <GuardRoute>
                <PSD2Receiving />
              </GuardRoute>
            </RequireAuth>
          }
        />
        <Route
          path={R.PSD2_VERIFIED}
          element={
            <RequireAuth>
              <GuardRoute>
                <PSD2Verified />
              </GuardRoute>
            </RequireAuth>
          }
        />
        <Route
          path={R.WAITING}
          element={
            <RequireAuth>
              <GuardRoute>
                <Waiting />
              </GuardRoute>
            </RequireAuth>
          }
        />
        <Route
          path={R.PSD2_ERROR}
          element={
            <RequireAuth>
              <GuardRoute>
                <PSD2Error />
              </GuardRoute>
            </RequireAuth>
          }
        />
        <Route
          path={R.DOCUMENT_UPLOAD_LATER}
          element={
            <RequireAuth>
              <GuardRoute>
                <DocumentUploadLater />
              </GuardRoute>
            </RequireAuth>
          }
        />
        <Route
          path={R.ADJUST_INCOME}
          element={
            <RequireAuth>
              <GuardRoute>
                <AdjustIncome />
              </GuardRoute>
            </RequireAuth>
          }
        />
        <Route
          element={
            <RequireAuth>
              <GuardRoute>
                <WelcomeBackIdentificationStatus />
              </GuardRoute>
            </RequireAuth>
          }
          path={R.PENDING}
        />
        <Route
          element={
            <RequireAuth>
              <GuardRoute>
                <ManualReview />
              </GuardRoute>
            </RequireAuth>
          }
          path={R.MANUAL_REVIEW}
        />
        <Route
          element={
            <RequireAuth>
              <GuardRoute>
                <ProofIncome />
              </GuardRoute>
            </RequireAuth>
          }
          path={R.INCOME_PROOF}
        />
        <Route
          element={
            <RequireAuth>
              <GuardRoute>
                <Approved />
              </GuardRoute>
            </RequireAuth>
          }
          path={R.APPROVED}
        />
        <Route path="/" element={<Navigate to={R.INTRO} />} />
        <Route path="*" element={<NotFound />} />
      </Route>
    </Route>,
  ),
);

export default function AppRouter() {
  useGoogleTagManager();

  return (
    <div className={styles.wrapper}>
      <RouterProvider router={router} />
    </div>
  );
}
